import { cloneDeep } from "lodash";
import * as SocialTypes from "../../../_constants/SocialType";
import * as PostEditorTabsTypes from "./PostEditorTabsTypes.constants";
import * as PostEditorErrorLevels from "./PostEditorErrorLevels.constants";
import * as PostTypes from "../../../_constants/PostTypes";
import * as PostAdditionalTypes from "../../../_constants/PostAdditionalTypes";
import { validUrlWithProtocol } from "../../../_utils/validator";
import { getMediaRatio } from "./utils";
import {
  countAtMentions,
  countHashtags,
} from "../AdvancedMessageField/AdvancedMessageCounter";

const restrictions = {
  [PostEditorTabsTypes.FACEBOOK]: {
    videos: {
      max: 1,
      // size_max: 10737418240,
      // size_max_str: "10GB",
      duration_max: 14400,
      duration_max_str: "4h",
    },
    photos: {
      max: 10,
    },
    comments: { max: 2 },
    message: {
      highlights: { hashtags: true, links: true },
    },
    scraper: { add: true },
    carousel: { add: true, max: 5 },
    mentions: { add: false, spaces: true },
    hashtags: {},
    additional: {
      types: [PostAdditionalTypes.CAROUSEL],
      typesRequired: [],
      typeFields: {
        [PostAdditionalTypes.CAROUSEL]: {
          endCard: {
            values: [false, true],
            emptyValue: false,
          },
          endCardLink: {
            conditions: (data) => data?.endCard,
            validate: (value) => !value || validUrlWithProtocol(value),
            required: true,
            emptyValue: "",
          },
          items: {
            validate: (value) => !value?.length || value.length > 1,
            required: true,
            emptyValue: [],
          },
        },
      },
    },
    validate: (accounts, post) => {
      const config = restrictions[PostEditorTabsTypes.FACEBOOK];
      const errors = [];
      const nonPagesAccounts = accounts.filter(
        (account) => account.social_type !== SocialTypes.FACEBOOK_PAGE
      );

      if (nonPagesAccounts.length) {
        if (post?.comments?.length) {
          errors.push({
            id: "facebook_comment_support",
            context: "global",
            level: PostEditorErrorLevels.WARNING,
          });
        }
      }

      if (post?.videos?.length) {
        const withInvalidDuration = post.videos
          .map((item) => {
            const duration = item?.meta?.duration;

            return duration && duration > config.videos.duration_max
              ? item.url
              : undefined;
          })
          .filter((x) => x !== undefined);

        if (withInvalidDuration.length) {
          errors.push({
            id: "facebook_video_duration",
            values: {
              duration_to: config.videos.duration_max_str,
            },
            context: "video",
            items: withInvalidDuration,
          });
        }
      }

      return errors;
    },
  },
  [PostEditorTabsTypes.LINKEDIN]: {
    videos: {
      max: 1,
    },
    photos: {
      max: 9,
    },
    comments: { max: 0 },
    message: {
      max: 3000,
      highlights: { hashtags: true, links: true },
    },
    scraper: { add: true, edit: true },
    mentions: { add: true },
    hashtags: {},
  },
  [PostEditorTabsTypes.VKONTAKTE]: {
    videos: {
      max: 0,
    },
    photos: {
      max: 10,
    },
    comments: { max: 0 },
    message: {
      highlights: { hashtags: true, links: true },
    },
    scraper: { add: true },
    mentions: { add: false, spaces: true },
    hashtags: {},
  },
  [PostEditorTabsTypes.GOOGLE]: {
    videos: {
      max: 0,
      // size_max: 78643200,
      // size_max_str: "75MB",
      height_min: 720,
      duration_max: 30,
      duration_max_str: "30s",
    },
    photos: {
      max: 1,
      // size_min: 10240,
      // size_min_str: "10KB",
      // size_max: 5242880,
      // size_max_str: "5MB",
      width_min: 250,
      height_min: 250,
    },
    comments: { max: 0 },
    message: {
      max: 1500,
      highlights: { links: true },
    },
    scraper: { add: false },
    mentions: { add: false },
    hashtags: {},
    additional: {
      types: [PostAdditionalTypes.POST],
      typesRequired: [PostAdditionalTypes.POST],
      typeFields: {
        [PostAdditionalTypes.POST]: {
          topicType: {
            required: true,
            values: ["STANDARD", "EVENT", "OFFER"],
            emptyValue: "STANDARD",
          },
          title: {
            conditions: (data) => ["EVENT", "OFFER"].includes(data?.topicType),
            validate: (value) => value?.length <= 58,
            required: true,
            emptyValue: "",
          },
          startDate: {
            conditions: (data) => ["EVENT", "OFFER"].includes(data?.topicType),
            required: true,
            emptyValue: "",
          },
          endDate: {
            conditions: (data) => ["EVENT", "OFFER"].includes(data?.topicType),
            required: true,
            emptyValue: "",
          },
          couponCode: {
            conditions: (data) => data?.topicType === "OFFER",
            emptyValue: "",
          },
          redeemOnlineUrl: {
            conditions: (data) => data?.topicType === "OFFER",
            validate: (value) => !value || validUrlWithProtocol(value),
            emptyValue: "",
          },
          termsConditions: {
            conditions: (data) => data?.topicType === "OFFER",
            emptyValue: "",
          },
          callToActionType: {
            conditions: (data) =>
              ["STANDARD", "EVENT"].includes(data?.topicType),
            values: ["", "BOOK", "ORDER", "SHOP", "LEARN_MORE", "SIGN_UP"],
            emptyValue: "",
          },
          callToActionUrl: {
            conditions: (data) => !!data?.callToActionType,
            validate: (value) => !value || validUrlWithProtocol(value),
            required: true,
            emptyValue: "",
          },
        },
      },
    },
    validate: (accounts, post) => {
      const config = restrictions[PostEditorTabsTypes.GOOGLE];
      const errors = [];

      if (post?.photos?.length) {
        const withInvalidResolution = post.photos
          .map((item) => {
            const width = item?.meta?.width;
            const height = item?.meta?.height;

            return width &&
              height &&
              (width < config.photos.width_min ||
                height < config.photos.height_min)
              ? item.url
              : undefined;
          })
          .filter((x) => x !== undefined);

        if (withInvalidResolution.length) {
          errors.push({
            id: "google_photo_resolution",
            values: {
              resolution_from: `${config.photos.width_min}x${config.photos.height_min}px`,
            },
            context: "photo",
            items: withInvalidResolution,
          });
        }
      }

      if (post?.videos?.length) {
        const withInvalidDuration = post.videos
          .map((item) => {
            const duration = item?.meta?.duration;

            return duration && duration > config.videos.duration_max
              ? item.url
              : undefined;
          })
          .filter((x) => x !== undefined);

        if (withInvalidDuration.length) {
          errors.push({
            id: "google_video_duration",
            values: {
              duration_to: config.videos.duration_max_str,
            },
            context: "video",
            items: withInvalidDuration,
          });
        }

        const withInvalidHeight = post.videos
          .map((item) => {
            const height = item?.meta?.height;

            return height && height < config.videos.height_min
              ? item.url
              : undefined;
          })
          .filter((x) => x !== undefined);

        if (withInvalidHeight.length) {
          errors.push({
            id: "google_video_resolution",
            values: {
              height_from: `${config.videos.height_min}px`,
            },
            context: "video",
            items: withInvalidHeight,
          });
        }
      }

      return errors;
    },
  },
  [PostEditorTabsTypes.YOUTUBE]: {
    videos: {
      max: 1,
      // size_max: 137438953472,
      // size_max_str: "128GB",
    },
    photos: {
      max: 0,
    },
    comments: { max: 0 },
    message: {
      highlights: { hashtags: true, links: true },
    },
    scraper: { add: false },
    mentions: { add: false, spaces: true },
    hashtags: {},
    additional: {
      types: [PostAdditionalTypes.POST_TYPE, PostAdditionalTypes.POST],
      typesRequired: [PostAdditionalTypes.POST_TYPE, PostAdditionalTypes.POST],
      typeFields: {
        [PostAdditionalTypes.POST_TYPE]: {
          value: {
            required: false,
            values: [PostTypes.VIDEO, PostTypes.SHORT],
            emptyValue: PostTypes.VIDEO,
          },
        },
        [PostAdditionalTypes.POST]: {
          title: {
            validate: (value) => value?.length <= 100,
            required: true,
            emptyValue: "",
          },
          keywords: {
            validate: (value) => value?.length <= 500,
            required: false,
            emptyValue: "",
          },
          privacyStatus: {
            values: ["", "private", "unlisted", "public"],
            required: false,
            emptyValue: "",
          },
          license: {
            values: ["", "youtube", "creativeCommon"],
            required: false,
            emptyValue: "",
          },
          selfDeclaredMadeForKids: {
            values: ["", "no", "yes"],
            required: false,
            emptyValue: "",
          },
        },
      },
    },
    validate: (accounts, post) => {
      const errors = [];

      if (!post?.videos?.length) {
        errors.push({
          id: "video_required",
          context: "global",
        });
      }

      return errors;
    },
    postTypeConfig: {
      [PostTypes.SHORT]: {
        videos: {
          max: 1,
          ratio_min: 9 / 16,
          ratio_min_str: "9:16",
          ratio_max: 1,
          ratio_max_str: "1:1",
          width_min: 600,
          height_min: 600,
          duration_max: 60,
          duration_max_str: "60s",
        },
        photos: {
          max: 0,
        },
        comments: { max: 0 },
        message: {
          highlights: { hashtags: true, links: true },
        },
        scraper: { add: false },
        mentions: { add: false, spaces: true },
        hashtags: {},
        additional: {
          types: [PostAdditionalTypes.POST],
          typesRequired: [PostAdditionalTypes.POST],
          typeFields: {
            [PostAdditionalTypes.POST]: {
              title: {
                validate: (value) => value?.length <= 100,
                required: true,
                emptyValue: "",
              },
              keywords: {
                validate: (value) => value?.length <= 500,
                required: false,
                emptyValue: "",
              },
              privacyStatus: {
                values: ["", "private", "unlisted", "public"],
                required: false,
                emptyValue: "",
              },
              license: {
                values: ["", "youtube", "creativeCommon"],
                required: false,
                emptyValue: "",
              },
              selfDeclaredMadeForKids: {
                values: ["", "no", "yes"],
                required: false,
                emptyValue: "",
              },
            },
          },
        },
        validate: (accounts, post) => {
          const config = restrictions[PostEditorTabsTypes.YOUTUBE].postTypeConfig[PostTypes.SHORT];
          const errors = [];

          if (!post?.videos?.length) {
            errors.push({
              id: "video_required",
              context: "global",
            });
          }

          if (post?.videos?.length) {
            const withInvalidDuration = post.videos
              .map((item) => {
                const duration = item?.meta?.duration;

                return duration &&
                  (duration > config.videos.duration_max)
                  ? item.url
                  : undefined;
              })
              .filter((x) => x !== undefined);

            if (withInvalidDuration.length) {
              errors.push({
                id: "youtube_short_video_duration",
                values: {
                  duration_to: config.videos.duration_max_str,
                },
                context: "video",
                items: withInvalidDuration,
              });
            }

            const withInvalidResolution = post.videos
              .map((item) => {
                const width = item?.meta?.width;
                const height = item?.meta?.height;

                return width &&
                  height &&
                  (width < config.videos.width_min ||
                    height < config.videos.height_min)
                  ? item.url
                  : undefined;
              })
              .filter((x) => x !== undefined);

            if (withInvalidResolution.length) {
              errors.push({
                id: "youtube_short_video_resolution",
                values: {
                  resolution_from: `${config.videos.width_min}x${config.videos.height_min}px`,
                },
                context: "video",
                items: withInvalidResolution,
              });
            }

            const withInvalidRatio = post.videos
              .map((item) => {
                const ratio = getMediaRatio(item?.meta);

                return ratio &&
                  (ratio > config.videos.ratio_max ||
                    ratio < config.videos.ratio_min)
                  ? item.url
                  : undefined;
              })
              .filter((x) => x !== undefined);

            if (withInvalidRatio.length) {
              errors.push({
                id: "youtube_short_video_ratio",
                values: {
                  ratio_from: config.videos.ratio_min_str,
                  ratio_to: config.videos.ratio_max_str,
                },
                context: "video",
                items: withInvalidRatio,
              });
            }
          }

          return errors;
        },
      },
    },
  },
  [PostEditorTabsTypes.INSTAGRAM]: {
    videos: {
      max: 10,
      // size_max: 104857600,
      // size_max_str: "100MB",
      ratio_min: 4 / 5,
      ratio_min_str: "4:5",
      ratio_max: 16 / 9,
      ratio_max_str: "16:9",
      height_max: 1920,
      duration_min: 3,
      duration_min_str: "3s",
      duration_max: 60,
      duration_max_str: "60s",
    },
    photos: {
      max: 10,
      // size_max: 8388608,
      // size_max_str: "8MB",
      ratio_min: 4 / 5,
      ratio_min_str: "4:5",
      ratio_max: 1.91,
      ratio_max_str: "1.91:1",
    },
    photosVideos: {
      max: 10,
    },
    comments: { max: 0 },
    message: {
      max: 2200,
      highlights: { atMentions: true, hashtags: true, links: false },
    },
    scraper: { add: false },
    mentions: { max: 20, add: false },
    hashtags: { max: 30 },
    additional: {
      types: [PostAdditionalTypes.POST_TYPE],
      typesRequired: [PostAdditionalTypes.POST_TYPE],
      typeFields: {
        [PostAdditionalTypes.POST_TYPE]: {
          value: {
            required: false,
            values: [PostTypes.POST, PostTypes.REEL, PostTypes.STORY],
            emptyValue: PostTypes.POST,
          },
        },
      },
    },
    validate: (accounts, post, { connectedDevices }) => {
      const config = restrictions[PostEditorTabsTypes.INSTAGRAM];
      const errors = [];
      const nonBusinessAccounts = accounts.filter(
        (account) => account.social_type !== SocialTypes.INSTAGRAM_BUSINESS
      );

      if (nonBusinessAccounts.length) {
        errors.push({
          id: "instagram_post_email_info",
          context: "global",
          level: PostEditorErrorLevels.INFO,
        });

        if (post?.comments?.length) {
          errors.push({
            id: "instagram_comment_support",
            context: "global",
            level: PostEditorErrorLevels.WARNING,
          });
        }
      }

      if (!post?.photos?.length && !post?.videos?.length) {
        errors.push({
          id: "photo_video_required",
          context: "global",
        });
      }

      if (post?.photos?.length) {
        const withInvalidRatio = post.photos
          .map((item) => {
            const ratio = getMediaRatio(item?.meta);

            return ratio &&
              (ratio > config.photos.ratio_max ||
                ratio < config.photos.ratio_min)
              ? item.url
              : undefined;
          })
          .filter((x) => x !== undefined);

        if (withInvalidRatio.length) {
          errors.push({
            id: "instagram_photo_ratio",
            values: {
              ratio_from: config.photos.ratio_min_str,
              ratio_to: config.photos.ratio_max_str,
            },
            context: "photo",
            items: withInvalidRatio,
          });
        }
      }

      if (post?.videos?.length) {
        const withInvalidDuration = post.videos
          .map((item) => {
            const duration = item?.meta?.duration;

            return duration &&
              (duration > config.videos.duration_max ||
                duration < config.videos.duration_min)
              ? item.url
              : undefined;
          })
          .filter((x) => x !== undefined);

        if (withInvalidDuration.length) {
          errors.push({
            id: "instagram_video_duration",
            values: {
              duration_from: config.videos.duration_min_str,
              duration_to: config.videos.duration_max_str,
            },
            context: "video",
            items: withInvalidDuration,
          });
        }

        const withInvalidHeight = post.videos
          .map((item) => {
            const height = item?.meta?.height;

            return height &&
              (height > config.videos.height_max ||
                height < config.videos.height_min)
              ? item.url
              : undefined;
          })
          .filter((x) => x !== undefined);

        if (withInvalidHeight.length) {
          errors.push({
            id: "instagram_video_resolution",
            values: {
              height_from: `${config.videos.height_min}px`,
              height_to: `${config.videos.height_max}px`,
            },
            context: "video",
            items: withInvalidHeight,
          });
        }

        const withInvalidRatio = post.videos
          .map((item) => {
            const ratio = getMediaRatio(item?.meta);

            return ratio &&
              (ratio > config.videos.ratio_max ||
                ratio < config.videos.ratio_min)
              ? item.url
              : undefined;
          })
          .filter((x) => x !== undefined);

        if (withInvalidRatio.length) {
          errors.push({
            id: "instagram_video_ratio",
            values: {
              ratio_from: config.videos.ratio_min_str,
              ratio_to: config.videos.ratio_max_str,
            },
            context: "video",
            items: withInvalidRatio,
          });
        }
      }

      const maxMentions = config.mentions.max;
      const maxHashtags = config.hashtags.max;

      if (countAtMentions(post) > maxMentions) {
        errors.push({
          id: "instagram_mentions_limit",
          values: {
            max: maxMentions,
          },
          context: "global",
        });
      }

      if (countHashtags(post) > maxHashtags) {
        errors.push({
          id: "instagram_hashtags_limit",
          values: {
            max: maxHashtags,
          },
          context: "global",
        });
      }

      return errors;
    },
    postTypeConfig: {
      [PostTypes.REEL]: {
        videos: {
          max: 1,
          duration_max: 90,
          duration_max_str: "90s",
        },
        photos: {
          max: 0,
        },
        comments: { max: 0 },
        message: {
          max: 2200,
          highlights: { atMentions: true, hashtags: true, links: false },
        },
        scraper: { add: false },
        mentions: { max: 20, add: false },
        hashtags: { max: 30 },
        additional: {},
        validate: (accounts, post) => {
          const config = restrictions[PostEditorTabsTypes.INSTAGRAM].postTypeConfig[PostTypes.REEL];
          const errors = [];

          errors.push({
            id: "instagram_reel_email_info",
            context: "global",
            level: PostEditorErrorLevels.INFO,
          });

          if (!post?.videos?.length) {
            errors.push({
              id: "video_required",
              context: "global",
            });
          }

          if (post?.videos?.length) {
            const withInvalidDuration = post.videos
              .map((item) => {
                const duration = item?.meta?.duration;

                return duration &&
                  (duration > config.videos.duration_max)
                  ? item.url
                  : undefined;
              })
              .filter((x) => x !== undefined);

            if (withInvalidDuration.length) {
              errors.push({
                id: "instagram_reel_video_duration",
                values: {
                  duration_to: config.videos.duration_max_str,
                },
                context: "video",
                level: PostEditorErrorLevels.WARNING,
                items: withInvalidDuration,
              });
            }
          }

          const maxMentions = config.mentions.max;
          const maxHashtags = config.hashtags.max;

          if (countAtMentions(post) > maxMentions) {
            errors.push({
              id: "instagram_mentions_limit",
              values: {
                max: maxMentions,
              },
              context: "global",
            });
          }

          if (countHashtags(post) > maxHashtags) {
            errors.push({
              id: "instagram_hashtags_limit",
              values: {
                max: maxHashtags,
              },
              context: "global",
            });
          }

          return errors;
        },
      },
      [PostTypes.STORY]: {
        videos: {
          max: 10,
          duration_max: 15,
          duration_max_str: "15s",
        },
        photos: {
          max: 10,
        },
        photosVideos: {
          max: 10,
        },
        comments: { max: 0 },
        message: { max: 0 },
        scraper: { add: false },
        additional: {},
        validate: (accounts, post) => {
          const config = restrictions[PostEditorTabsTypes.INSTAGRAM].postTypeConfig[PostTypes.STORY];
          const errors = [];

          errors.push({
            id: "instagram_story_email_info",
            context: "global",
            level: PostEditorErrorLevels.INFO,
          });

          if (!post?.photos?.length && !post?.videos?.length) {
            errors.push({
              id: "photo_video_required",
              context: "global",
            });
          }

          if (post?.videos?.length) {
            const withInvalidDuration = post.videos
              .map((item) => {
                const duration = item?.meta?.duration;

                return duration &&
                  (duration > config.videos.duration_max)
                  ? item.url
                  : undefined;
              })
              .filter((x) => x !== undefined);

            if (withInvalidDuration.length) {
              errors.push({
                id: "instagram_story_video_duration",
                values: {
                  duration_to: config.videos.duration_max_str,
                },
                context: "video",
                level: PostEditorErrorLevels.WARNING,
                items: withInvalidDuration,
              });
            }
          }

          return errors;
        },
      },
    },
  },
  [PostEditorTabsTypes.TWITTER]: {
    videos: {
      max: 1,
      // size_max: 536870912,
      // size_max_str: "512MB",
      ratio_min: 1 / 3,
      ratio_min_str: "1:3",
      ratio_max: 3,
      ratio_max_str: "3:1",
      width_min: 32,
      width_max: 1280,
      height_min: 32,
      height_max: 1280,
      duration_min: 0.5,
      duration_min_str: "0.5s",
      duration_max: 140,
      duration_max_str: "140s",
    },
    photos: {
      max: 4,
      // size_max: 5242880,
      // size_max_str: "5MB",
    },
    comments: { max: 0 },
    message: {
      max: 280,
      highlights: { atMentions: true, hashtags: true, links: true },
    },
    scraper: { add: false, previewOnly: true },
    mentions: { add: true },
    hashtags: {},
    validate: (accounts, post) => {
      const config = restrictions[PostEditorTabsTypes.TWITTER];
      const errors = [];

      if (accounts.length > 1) {
        const account = accounts[0];

        errors.push({
          id: "twitter_single_account_support",
          values: {
            name: account.name,
            nickname: account.nickname,
          },
          context: "global",
          level: PostEditorErrorLevels.WARNING,
        });
      }

      if (post?.videos?.length) {
        const withInvalidDuration = post.videos
          .map((item) => {
            const duration = item?.meta?.duration;

            return duration &&
              (duration > config.videos.duration_max ||
                duration < config.videos.duration_min)
              ? item.url
              : undefined;
          })
          .filter((x) => x !== undefined);

        if (withInvalidDuration.length) {
          errors.push({
            id: "twitter_video_duration",
            values: {
              duration_from: config.videos.duration_min_str,
              duration_to: config.videos.duration_max_str,
            },
            context: "video",
            items: withInvalidDuration,
          });
        }

        const withInvalidResolution = post.videos
          .map((item) => {
            const width = item?.meta?.width;
            const height = item?.meta?.height;

            return width &&
              height &&
              (width > config.videos.width_max ||
                width < config.videos.width_min ||
                height > config.videos.height_max ||
                height < config.videos.height_min)
              ? item.url
              : undefined;
          })
          .filter((x) => x !== undefined);

        if (withInvalidResolution.length) {
          errors.push({
            id: "twitter_video_resolution",
            values: {
              resolution_from: `${config.videos.width_min}x${config.videos.height_min}px`,
              resolution_to: `${config.videos.width_max}x${config.videos.height_max}px`,
            },
            context: "video",
            items: withInvalidResolution,
          });
        }

        const withInvalidRatio = post.videos
          .map((item) => {
            const ratio = getMediaRatio(item?.meta);

            return ratio &&
              (ratio > config.videos.ratio_max ||
                ratio < config.videos.ratio_min)
              ? item.url
              : undefined;
          })
          .filter((x) => x !== undefined);

        if (withInvalidRatio.length) {
          errors.push({
            id: "twitter_video_ratio",
            values: {
              ratio_from: config.videos.ratio_min_str,
              ratio_to: config.videos.ratio_max_str,
            },
            context: "video",
            items: withInvalidRatio,
          });
        }
      }

      return errors;
    },
  },
  [PostEditorTabsTypes.PINTEREST]: {
    videos: {
      max: 1,
      // size_max: 2147483648,
      // size_max_str: "2GB",
      ratio_min: 1 / 2,
      ratio_min_str: "1:2",
      ratio_max: 1.91,
      ratio_max_str: "1.91:1",
      width_min: 200,
      height_min: 300,
      duration_min: 4,
      duration_min_str: "4s",
      duration_max: 900,
      duration_max_str: "10min",
    },
    photos: {
      max: 1,
      // size_max: 20971520,
      // size_max_str: "20MB",
      width_min: 200,
      height_min: 300,
    },
    comments: { max: 0 },
    message: {
      max: 500,
      highlights: { atMentions: true, hashtags: false, links: false },
    },
    scraper: { add: false },
    mentions: { add: false },
    hashtags: {},
    additional: {
      types: [PostAdditionalTypes.POST],
      typesRequired: [PostAdditionalTypes.POST],
      typeFields: {
        [PostAdditionalTypes.POST]: {
          title: {
            emptyValue: "",
          },
          imageAlt: {
            emptyValue: "",
          },
          link: {
            validate: (value) => !value || validUrlWithProtocol(value),
            emptyValue: "",
          },
        },
      },
    },
    validate: (accounts, post) => {
      const config = restrictions[PostEditorTabsTypes.PINTEREST];
      const errors = [];

      if (!post?.photos?.length && !post?.videos?.length) {
        errors.push({
          id: "photo_video_required",
          context: "global",
        });
      }

      if (post?.photos?.length) {
        const withInvalidResolution = post.photos
          .map((item) => {
            const width = item?.meta?.width;
            const height = item?.meta?.height;

            return width &&
              height &&
              (width < config.photos.width_min ||
                height < config.photos.height_min)
              ? item.url
              : undefined;
          })
          .filter((x) => x !== undefined);

        if (withInvalidResolution.length) {
          errors.push({
            id: "pinterest_photo_resolution",
            values: {
              resolution_from: `${config.photos.width_min}x${config.photos.height_min}px`,
            },
            context: "photo",
            items: withInvalidResolution,
          });
        }
      }

      if (post?.videos?.length) {
        const withInvalidDuration = post.videos
          .map((item) => {
            const duration = item?.meta?.duration;

            return duration &&
              (duration > config.videos.duration_max ||
                duration < config.videos.duration_min)
              ? item.url
              : undefined;
          })
          .filter((x) => x !== undefined);

        if (withInvalidDuration.length) {
          errors.push({
            id: "pinterest_video_duration",
            values: {
              duration_from: config.videos.duration_min_str,
              duration_to: config.videos.duration_max_str,
            },
            context: "video",
            items: withInvalidDuration,
          });
        }

        const withInvalidResolution = post.videos
          .map((item) => {
            const width = item?.meta?.width;
            const height = item?.meta?.height;

            return width &&
              height &&
              (width < config.videos.width_min ||
                height < config.videos.height_min)
              ? item.url
              : undefined;
          })
          .filter((x) => x !== undefined);

        if (withInvalidResolution.length) {
          errors.push({
            id: "pinterest_video_resolution",
            values: {
              resolution_from: `${config.videos.width_min}x${config.videos.height_min}px`,
            },
            context: "video",
            items: withInvalidResolution,
          });
        }

        const withInvalidRatio = post.videos
          .map((item) => {
            const ratio = getMediaRatio(item?.meta);

            return ratio &&
              (ratio > config.videos.ratio_max ||
                ratio < config.videos.ratio_min)
              ? item.url
              : undefined;
          })
          .filter((x) => x !== undefined);

        if (withInvalidRatio.length) {
          errors.push({
            id: "pinterest_video_ratio",
            values: {
              ratio_from: config.videos.ratio_min_str,
              ratio_to: config.videos.ratio_max_str,
            },
            context: "video",
            items: withInvalidRatio,
          });
        }
      }

      return errors;
    },
  },
  [PostEditorTabsTypes.TIKTOK]: {
    videos: {
      max: 1,
      // size_max: 52428800,
      // size_max_str: "50MB",
      // width_min: 720,
      // height_min: 1280,
      duration_min: 3,
      duration_min_str: "3s",
      // duration_max: 180,
      // duration_max_str: "180s",
      duration_max: 60,
      duration_max_str: "60s",
    },
    photos: {
      max: 0,
    },
    comments: { max: 0 },
    message: {
      max: 150,
      highlights: { atMentions: true, hashtags: true, links: false },
    },
    scraper: { add: false },
    mentions: { add: false },
    hashtags: {},
    validate: (accounts, post, { connectedDevices }) => {
      const config = restrictions[PostEditorTabsTypes.TIKTOK];
      const errors = [];

      errors.push({
        id: "tiktok_email_info",
        context: "global",
        level: PostEditorErrorLevels.INFO,
      });

      if (!post?.videos?.length) {
        errors.push({
          id: "video_required",
          context: "global",
        });
      }

      if (post?.videos?.length) {
        const withInvalidDuration = post.videos
          .map((item) => {
            const duration = item?.meta?.duration;

            return duration &&
              (duration > config.videos.duration_max ||
                duration < config.videos.duration_min)
              ? item.url
              : undefined;
          })
          .filter((x) => x !== undefined);

        if (withInvalidDuration.length) {
          errors.push({
            id: "tiktok_video_duration",
            values: {
              duration_from: config.videos.duration_min_str,
              duration_to: config.videos.duration_max_str,
            },
            context: "video",
            items: withInvalidDuration,
          });
        }

        // const withInvalidResolution = post.videos
        //   .map((item) => {
        //     const width = item?.meta?.width;
        //     const height = item?.meta?.height;

        //     return width &&
        //       height &&
        //       (width < config.videos.width_min ||
        //         height < config.videos.height_min)
        //       ? item.url
        //       : undefined;
        //   })
        //   .filter((x) => x !== undefined);

        // if (withInvalidResolution.length) {
        //   errors.push({
        //     id: "tiktok_video_resolution",
        //     values: {
        //       resolution_from: `${config.videos.width_min}x${config.videos.height_min}px`,
        //     },
        //     context: "video",
        //     items: withInvalidResolution,
        //   });
        // }
      }

      return errors;
    },
  },
};

const getTabConfig = (tab, postType) => {
  const { postTypeConfig, ...config } = restrictions?.[tab] || {};

  if (!postType || !config?.additional?.types?.includes(PostAdditionalTypes.POST_TYPE) || !config?.additional?.typeFields?.[PostAdditionalTypes.POST_TYPE] || postType === config?.additional?.typeFields?.[PostAdditionalTypes.POST_TYPE]?.value?.emptyValue) {
    return config;
  }

  const additional = postTypeConfig?.[postType]?.additional;

  return {
    ...(postTypeConfig?.[postType] || {}),
    additional: {
      ...(additional || {}),
      types: [...(additional?.types || []), PostAdditionalTypes.POST_TYPE],
      typesRequired: [...(additional?.typesRequired || []), PostAdditionalTypes.POST_TYPE],
      typeFields: {
        ...(additional?.typeFields || {}),
        [PostAdditionalTypes.POST_TYPE]: config?.additional?.typeFields?.[PostAdditionalTypes.POST_TYPE],
      },
    },
  };
};

export const getBaseMaxVideos = () => {
  const values = Object.values(restrictions)
    .map((config) => config?.videos?.max)
    .filter((value) => value !== undefined);

  return values.length ? Math.max(...values) : undefined;
};

export const getBaseMaxPhotos = () => {
  const values = Object.values(restrictions)
    .map((config) => config?.photos?.max)
    .filter((value) => value !== undefined);

  return values.length ? Math.max(...values) : undefined;
};

export const getBaseMaxPhotosVideos = () => {
  const values = [
    getBaseMaxVideos(),
    getBaseMaxPhotos(),
    ...Object.values(restrictions).map((config) => config?.photosVideos?.max),
  ].filter((value) => value !== undefined);

  return values.length ? Math.max(...values) : undefined;
};

export const getBaseMaxComments = () => {
  const values = Object.values(restrictions)
    .map((config) => config?.comments?.max)
    .filter((value) => value !== undefined);

  return values.length ? Math.max(...values) : undefined;
};

export const getMaxVideos = (tab, postType) => getTabConfig(tab, postType)?.videos?.max;

export const getMaxPhotos = (tab, postType) => getTabConfig(tab, postType)?.photos?.max;

export const getMaxPhotosVideos = (tab, postType) => {
  const values = [
    getMaxVideos(),
    getMaxPhotos(),
    getTabConfig(tab, postType)?.photosVideos?.max,
  ].filter((value) => value !== undefined);

  return values.length ? Math.max(...values) : undefined;
};

export const getMaxComments = (tab, postType) => getTabConfig(tab, postType)?.comments?.max;

export const getMaxMessageChars = (tab, postType) => getTabConfig(tab, postType)?.message?.max;

export const getMaxMentions = (tab, postType) => getTabConfig(tab, postType)?.mentions?.max;

export const getMaxHashtags = (tab, postType) => getTabConfig(tab, postType)?.hashtags?.max;

export const canAddScraper = (tab, postType) => getTabConfig(tab, postType)?.scraper?.add;

export const canEditScraperDetails = (tab, postType) =>
  getTabConfig(tab, postType)?.scraper?.edit;

export const haveMessageLinkPreview = (tab, postType) =>
  getTabConfig(tab, postType)?.scraper?.previewOnly;

export const canAddCarousel = (tab, postType) => getTabConfig(tab, postType)?.carousel?.add;

export const getMaxCarouselItems = (tab, postType) => getTabConfig(tab, postType)?.carousel?.max;

export const canAddMentions = (tab, postType) => getTabConfig(tab, postType)?.mentions?.add;

export const canAddMentionsWithSpaces = (tab, postType) => getTabConfig(tab, postType)?.mentions?.spaces;

export const haveHighlightedAtMentions = (tab, postType) => getTabConfig(tab, postType)?.message?.highlights?.atMentions;

export const haveHighlightedHashtags = (tab, postType) => getTabConfig(tab, postType)?.message?.highlights?.hashtags;

export const haveHighlightedLinks = (tab, postType) => getTabConfig(tab, postType)?.message?.highlights?.links;

export const haveAdditionalType = (tab, postType, type) => getTabConfig(tab, postType)?.additional?.types?.includes(type);

export const getAdditionalTypeFields = (tab, postType, type, values) => {
  const fields = {};

  if (!haveAdditionalType(tab, postType, type)) {
    return fields;
  }

  const typeFields = getTabConfig(tab, postType)?.additional?.typeFields?.[type] || {};

  Object.keys(typeFields).forEach((field) => {
    const fieldData = cloneDeep(typeFields[field]);

    if (
      fieldData &&
      (!fieldData?.conditions || fieldData.conditions?.(values))
    ) {
      const defaultValue =
        fieldData.emptyValue !== undefined ? fieldData.emptyValue : null;
      const value = values?.[field] || defaultValue;
      const errorRequired = fieldData.required && !value;
      const errorValidate = fieldData.validate && !fieldData.validate?.(value);

      fields[field] = {
        ...fieldData,
        value,
        isInvalid: errorRequired || errorValidate,
        isInvalidRequired: errorRequired,
      };
    }
  });

  return fields;
};

export const getAdditionalTypesFields = (tab, postType, values) => {
  const types = getTabConfig(tab, postType)?.additional?.types || [];
  const typesRequired = getTabConfig(tab, postType)?.additional?.typesRequired || [];
  const fieldsByType = {};

  types.forEach((type) => {
    const typeValues = values?.find(
      (item) => item.source === tab && item.type === type
    );

    if (typeValues || typesRequired.includes(type)) {
      fieldsByType[type] = getAdditionalTypeFields(tab, postType, type, typeValues);
    }
  });

  return fieldsByType;
};

export const getAdditionalErrors = (tab, postType, values) => {
  const types = getTabConfig(tab, postType)?.additional?.types || [];
  const errors = [];

  types.forEach((type) => {
    const typeValues = values?.find(
      (item) => item.source === tab && item.type === type
    );
    const typeFields = getAdditionalTypeFields(tab, postType, type, typeValues);

    Object.keys(typeFields).forEach((field) => {
      if (typeFields[field].isInvalid) {
        if (typeFields[field].isInvalidRequired) {
          errors.push({
            id: `additional_${tab}_${type}_${field}_required`,
          });
        } else {
          errors.push({
            id: `additional_${tab}_${type}_${field}_invalid`,
          });
        }
      }
    });
  });

  return errors;
};

export const getErrors = async (tab, postType, accounts, post, custom) => {
  const errors =
    (await getTabConfig(tab, postType)?.validate?.(accounts, post, custom)) || [];
  const additionalErrors = getAdditionalErrors(tab, postType, post?.additional) || [];

  return [...errors, ...additionalErrors];
};
